import * as React from 'react';

import * as ramda from 'ramda';

import {StackLegacy} from 'shells/layout/stack/stack-legacy';
import {LoadingPage} from 'shells/loading-page/loading-page';
import {SettingsGroup} from 'shells/settings/settings-group';
import {SettingsHeader} from 'shells/settings/settings-header';

import type {VisitorIqFilterSettingsUpdateInput as SelectedVisitorIqFilterSettings} from 'nutshell-graphql-types';

import {
    getAllSettingsFromFilterSettings,
    getHeaderAndSubheaderBySettingsGroupKey,
    getSelectedFilterSettings,
    handleSettingSelectionChange,
} from '../../people-iq/settings/helpers';
import type {SettingsGroupKey} from '../../people-iq/settings/helpers';

import {useGetVisitorIqFilterSettings} from './settings/graphql/hooks/use-get-visitor-iq-filter-settings';
import {useUpdateVisitorIqFilterSettings} from './settings/graphql/hooks/use-update-visitor-iq-filter-settings';
import {VisitorIqUrlBlocklistSettings} from './visitor-iq-url-blocklist-settings';

export function VisitorsSettingsPage() {
    const {visitorIqFilterSettings} = useGetVisitorIqFilterSettings();
    const {
        updateVisitorIqFilterSettings,
        isLoading: isSaving,
        error,
    } = useUpdateVisitorIqFilterSettings();

    const [selectedSettings, setSelectedSettings] = React.useState<
        SelectedVisitorIqFilterSettings | null | undefined
    >(undefined);

    const allSettings =
        visitorIqFilterSettings && getAllSettingsFromFilterSettings(visitorIqFilterSettings);

    React.useEffect(() => {
        if (visitorIqFilterSettings) {
            // @ts-expect-error
            setSelectedSettings(getSelectedFilterSettings(visitorIqFilterSettings));
        }
    }, [visitorIqFilterSettings]);

    return (
        <div>
            <SettingsHeader
                title='Settings'
                subtitle='Use these filters to refine your Visitor IQ results'
                onSave={() => {
                    if (selectedSettings) {
                        updateVisitorIqFilterSettings(selectedSettings);
                    }
                }}
                isSaving={isSaving}
                error={error}
                disabled={
                    ramda.equals(
                        selectedSettings,
                        visitorIqFilterSettings &&
                            getSelectedFilterSettings(visitorIqFilterSettings)
                    ) || isSaving
                }
            />
            {allSettings && selectedSettings ? (
                <StackLegacy spacing={12}>
                    {/* @ts-expect-error */}
                    {Object.keys(allSettings).map((settingsGroupKey: SettingsGroupKey) => (
                        <SettingsGroup
                            key={settingsGroupKey}
                            {...getHeaderAndSubheaderBySettingsGroupKey(settingsGroupKey)}
                            // @ts-expect-error
                            allGroupSettings={allSettings[settingsGroupKey]}
                            selectedGroupSettings={selectedSettings[settingsGroupKey]}
                            onSettingChange={(settingId, checked) =>
                                handleSettingSelectionChange(
                                    setSelectedSettings,
                                    settingsGroupKey,
                                    settingId,
                                    checked
                                )
                            }
                            disabled={isSaving}
                        />
                    ))}
                    <VisitorIqUrlBlocklistSettings
                        urls={
                            selectedSettings &&
                            'urlBlocklist' in selectedSettings &&
                            Array.isArray(selectedSettings.urlBlocklist)
                                ? selectedSettings.urlBlocklist
                                : []
                        }
                        onChange={(urls) => {
                            setSelectedSettings((prev) => {
                                if (!prev) {
                                    return prev;
                                }
                                return {...prev, urlBlocklist: urls};
                            });
                        }}
                        disabled={isSaving}
                    />
                </StackLegacy>
            ) : (
                <LoadingPage padding={0} />
            )}
        </div>
    );
}
